import { ChakraAvatar } from '@web/components/atoms/Avatar'
import { SearchInput } from '@web/components/atoms/Input'
import { Box, HStack } from '@web/components/atoms/Layout'
import { Span } from '@web/components/atoms/Typography'
import { createListCollection } from '@chakra-ui/react/collection'
import { Select as ChakraSelect, useSelectContext } from '@chakra-ui/react/select'
import { ProfileData } from '@web/graphql-types'
import { useMemo, useState } from 'react'

type ProfileSelectorProps = {
  label: string
  isRequired?: boolean
  placeholder?: string | null
  teams: ProfileData[]
  onChange: (profileId: string | null) => void
  selectedProfileId: string
}

export type PageProfileSelectorProps = Pick<ProfileSelectorProps, 'label' | 'isRequired' | 'placeholder'>

const SelectValue = ({ placeholder }: { placeholder?: string | null }) => {
  const select = useSelectContext()
  const items = select.selectedItems as Array<ProfileData>
  const { name, logoUrl, id } = items[0]
  return (
    <ChakraSelect.ValueText placeholder={placeholder ?? ''}>
      <HStack>
        {id && (
          <ChakraAvatar.Root shape="rounded" size="2xs">
            <ChakraAvatar.Image src={logoUrl ?? ''} alt={name} />
            <ChakraAvatar.Fallback name={name} />
          </ChakraAvatar.Root>
        )}
        {name}
      </HStack>
    </ChakraSelect.ValueText>
  )
}

const ProfileSelector = ({
  label,
  isRequired,
  placeholder,
  teams = [],
  onChange,
  selectedProfileId,
}: ProfileSelectorProps) => {
  const [search, setSearch] = useState('')
  const [searchInputKey, setSearchInputKey] = useState(0)
  const placeholderItem = useMemo(
    () => ({ id: '', name: `${placeholder ?? 'Select team...'}`, logoUrl: '' }),
    [placeholder],
  )

  const collection = useMemo(
    () =>
      createListCollection({
        items: [placeholderItem, ...teams],
        itemToString: (item) => item.name,
        itemToValue: (item) => item.id,
      }),
    [teams, placeholderItem],
  )

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return collection.items
    }

    return collection.items.filter((item) => item.id && item.name.toLowerCase().includes(query))
  }, [collection.items, search])

  return (
    <ChakraSelect.Root
      collection={collection}
      size="sm"
      width="100%"
      positioning={{ sameWidth: true }}
      value={[selectedProfileId ?? '']}
      onValueChange={(e) => {
        onChange(e.value[0] || null)
      }}
      onOpenChange={(e) => {
        if (!e.open) {
          setSearch('')
          setSearchInputKey((key) => key + 1)
        }
      }}
    >
      <ChakraSelect.HiddenSelect />
      <ChakraSelect.Label>
        {label}
        {isRequired && <Span color="#ef4444"> *</Span>}
      </ChakraSelect.Label>
      <ChakraSelect.Control bgColor="ge.white" color="ge.text.primary">
        <ChakraSelect.Trigger>
          <SelectValue placeholder={placeholder} />
        </ChakraSelect.Trigger>
        <ChakraSelect.IndicatorGroup>
          <ChakraSelect.Indicator />
        </ChakraSelect.IndicatorGroup>
      </ChakraSelect.Control>
      <ChakraSelect.Positioner>
        <ChakraSelect.Content color="ge.text.primary">
          <Box
            w="100%"
            px="4px"
            py="4px"
            position="sticky"
            top="0"
            zIndex={1}
            bgColor="inherit"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <SearchInput
              key={searchInputKey}
              w="100%"
              placeholder="Search teams..."
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
          {filteredItems.length === 0 ? (
            <Box px="12px" py="8px" color="ge.text.secondary">
              No teams found
            </Box>
          ) : (
            filteredItems.map((item) => (
              <ChakraSelect.Item item={item} key={item.id} justifyContent="flex-start">
                {item.id && (
                  <ChakraAvatar.Root shape="rounded" size="2xs">
                    <ChakraAvatar.Image src={item.logoUrl ?? ''} alt={item.name} />
                    <ChakraAvatar.Fallback name={item.name} />
                  </ChakraAvatar.Root>
                )}
                {item.name}
                <ChakraSelect.ItemIndicator />
              </ChakraSelect.Item>
            ))
          )}
        </ChakraSelect.Content>
      </ChakraSelect.Positioner>
    </ChakraSelect.Root>
  )
}

export default ProfileSelector
